import * as PIXI from 'pixi.js';
import { PolygonCollider } from '@/entities/PolygonCollider';

export interface EditorConfig {
  camera: {
    container: PIXI.Container;
    getWorldPosition: (screenX: number, screenY: number) => { x: number; y: number };
  };
  onSave?: (polygons: Array<{ points: number[] }>) => void;
}

export class ColliderEditorTool {
  private isActive = false;
  private currentPoints: number[] = [];
  private previewGraphics: PIXI.Graphics;
  private vertexGraphics: PIXI.Graphics;
  private polygonColliders: PolygonCollider[] = [];
  private camera: EditorConfig['camera'];
  private onSave?: (polygons: Array<{ points: number[] }>) => void;
  private instructionText: PIXI.Text;
  private mousePosition = { x: 0, y: 0 };
  private isMouseDown = false;

  constructor(config: EditorConfig) {
    this.camera = config.camera;
    this.onSave = config.onSave;

    // Create graphics for preview
    this.previewGraphics = new PIXI.Graphics();
    this.vertexGraphics = new PIXI.Graphics();

    // Create instruction text
    this.instructionText = new PIXI.Text('', {
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0xffffff,
      stroke: { color: 0x000000, width: 3 },
    });
    this.instructionText.x = 10;
    this.instructionText.y = 60;
    this.instructionText.visible = false;
  }

  /**
   * Toggle the editor mode on/off
   */
  public toggle(): boolean {
    this.isActive = !this.isActive;

    if (this.isActive) {
      this.activate();
    } else {
      this.deactivate();
    }

    return this.isActive;
  }

  /**
   * Check if the editor is currently active
   */
  public isEditorActive(): boolean {
    return this.isActive;
  }

  /**
   * Activate the editor
   */
  private activate(): void {
    console.log('Collider Editor Activated');
    this.instructionText.visible = true;
    this.updateInstructions();

    // Add graphics to camera world (so they move with the world)
    this.camera.container.addChild(this.previewGraphics);
    this.camera.container.addChild(this.vertexGraphics);

    // Add instruction text to stage (fixed position, not part of world)
    // We'll handle this in the Game.ts integration
  }

  /**
   * Deactivate the editor
   */
  private deactivate(): void {
    console.log('Collider Editor Deactivated');
    this.instructionText.visible = false;
    this.currentPoints = [];
    this.previewGraphics.clear();
    this.vertexGraphics.clear();

    // Remove graphics from camera
    if (this.previewGraphics.parent) {
      this.previewGraphics.parent.removeChild(this.previewGraphics);
    }
    if (this.vertexGraphics.parent) {
      this.vertexGraphics.parent.removeChild(this.vertexGraphics);
    }
  }

  /**
   * Update instruction text based on current state
   */
  private updateInstructions(): void {
    const numVertices = this.currentPoints.length / 2;
    if (numVertices === 0) {
      this.instructionText.text =
        'COLLIDER EDITOR MODE\n' +
        'Click to place vertices (min 3)\n' +
        'Enter: Complete polygon | Esc: Cancel | E: Export | C: Clear all';
    } else {
      this.instructionText.text =
        `COLLIDER EDITOR MODE (${numVertices} vertices)\n` +
        'Click to add vertex | Enter: Complete | Esc: Cancel | E: Export | C: Clear all';
    }
  }

  /**
   * Handle mouse move event
   */
  public handleMouseMove(screenX: number, screenY: number): void {
    if (!this.isActive) return;

    const worldPos = this.camera.getWorldPosition(screenX, screenY);
    this.mousePosition = worldPos;
    this.updatePreview();
  }

  /**
   * Handle mouse down event
   */
  public handleMouseDown(): void {
    this.isMouseDown = true;
  }

  /**
   * Handle mouse up event (this is where we add points to prevent accidental double-clicks)
   */
  public handleMouseUp(screenX: number, screenY: number): void {
    if (!this.isActive || !this.isMouseDown) return;

    this.isMouseDown = false;
    const worldPos = this.camera.getWorldPosition(screenX, screenY);
    
    // Add point
    this.currentPoints.push(worldPos.x, worldPos.y);
    this.updateInstructions();
    this.updatePreview();
    
    console.log(`Added vertex at (${Math.round(worldPos.x)}, ${Math.round(worldPos.y)})`);
  }

  /**
   * Handle keyboard input
   */
  public handleKeyPress(key: string): void {
    if (!this.isActive) return;

    switch (key.toLowerCase()) {
      case 'enter':
        this.completePolygon();
        break;
      case 'escape':
        this.cancelCurrentPolygon();
        break;
      case 'e':
        this.exportPolygons();
        break;
      case 'c':
        this.clearAllPolygons();
        break;
    }
  }

  /**
   * Update the preview graphics
   */
  private updatePreview(): void {
    this.previewGraphics.clear();
    this.vertexGraphics.clear();

    if (this.currentPoints.length === 0) return;

    // Draw existing vertices
    this.vertexGraphics.lineStyle(0);
    for (let i = 0; i < this.currentPoints.length; i += 2) {
      this.vertexGraphics.beginFill(0x00ff00, 0.8);
      this.vertexGraphics.drawCircle(this.currentPoints[i], this.currentPoints[i + 1], 5);
      this.vertexGraphics.endFill();
    }

    // Draw lines between vertices
    if (this.currentPoints.length >= 2) {
      this.previewGraphics.lineStyle(2, 0x00ff00, 0.8);
      this.previewGraphics.moveTo(this.currentPoints[0], this.currentPoints[1]);

      for (let i = 2; i < this.currentPoints.length; i += 2) {
        this.previewGraphics.lineTo(this.currentPoints[i], this.currentPoints[i + 1]);
      }

      // Draw line to mouse position
      this.previewGraphics.lineTo(this.mousePosition.x, this.mousePosition.y);

      // If we have at least 3 vertices, show potential closing line
      if (this.currentPoints.length >= 6) {
        this.previewGraphics.lineStyle(2, 0x00ff00, 0.4);
        this.previewGraphics.lineTo(this.currentPoints[0], this.currentPoints[1]);
      }
    }

    // Draw mouse cursor
    this.vertexGraphics.lineStyle(2, 0x00ff00, 0.5);
    this.vertexGraphics.drawCircle(this.mousePosition.x, this.mousePosition.y, 8);
  }

  /**
   * Complete the current polygon
   */
  private completePolygon(): void {
    if (this.currentPoints.length < 6) {
      console.warn('Need at least 3 vertices to create a polygon');
      return;
    }

    // Create polygon collider
    const polygonCollider = new PolygonCollider({
      points: [...this.currentPoints],
      color: 0xff0000,
      visible: true,
    });

    this.polygonColliders.push(polygonCollider);
    this.camera.container.addChild(polygonCollider.sprite);

    console.log(`Polygon created with ${this.currentPoints.length / 2} vertices`);
    console.log('Points:', this.currentPoints);

    // Reset for next polygon
    this.currentPoints = [];
    this.updateInstructions();
    this.updatePreview();
  }

  /**
   * Cancel the current polygon being drawn
   */
  private cancelCurrentPolygon(): void {
    if (this.currentPoints.length > 0) {
      console.log('Cancelled current polygon');
      this.currentPoints = [];
      this.updateInstructions();
      this.updatePreview();
    }
  }

  /**
   * Clear all polygons
   */
  private clearAllPolygons(): void {
    for (const collider of this.polygonColliders) {
      if (collider.sprite.parent) {
        collider.sprite.parent.removeChild(collider.sprite);
      }
    }
    this.polygonColliders = [];
    console.log('Cleared all polygons');
  }

  /**
   * Export all polygons to console and trigger save callback
   */
  private exportPolygons(): void {
    if (this.polygonColliders.length === 0) {
      console.warn('No polygons to export');
      return;
    }

    const polygonData = this.polygonColliders.map(collider => ({
      points: collider.points,
    }));

    console.log('=== EXPORTED POLYGON COLLIDERS ===');
    console.log(JSON.stringify(polygonData, null, 2));
    console.log('===================================');
    console.log('Copy the above JSON and add it to your ColliderConfig.ts file');

    // Trigger save callback if provided
    if (this.onSave) {
      this.onSave(polygonData);
    }

    // Also download as a file
    this.downloadPolygonData(polygonData);
  }

  /**
   * Download polygon data as a JSON file
   */
  private downloadPolygonData(polygonData: Array<{ points: number[] }>): void {
    const dataStr = JSON.stringify(polygonData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `colliders-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    console.log('Polygon data downloaded as JSON file');
  }

  /**
   * Get the instruction text container
   */
  public getInstructionText(): PIXI.Text {
    return this.instructionText;
  }

  /**
   * Get all polygon colliders
   */
  public getPolygonColliders(): PolygonCollider[] {
    return this.polygonColliders;
  }

  /**
   * Destroy the editor and clean up resources
   */
  public destroy(): void {
    this.deactivate();
    this.clearAllPolygons();
    this.previewGraphics.destroy();
    this.vertexGraphics.destroy();
    this.instructionText.destroy();
  }
}
